import type React from "react";
import type { Channel } from "@/types/channel/channel";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useCreateChannel } from "@/hooks/channel/useCreateChannel";
import { useUpdateChannel } from "@/hooks/channel/useUpdateChannel";
import { useToast } from "@/hooks/ui/use-toast";

const channelSchema = z.object({
	name: z
		.string()
		.min(2, "2文字以上で入力してください")
		.max(50, "50文字以内で入力してください"),
	description: z.string().max(200, "200文字以内で入力してください").optional(),
	type: z.enum(["ANNOUNCEMENT", "TEXT"]),
	emoji: z.string().max(10, "絵文字は1つまでです").optional(),
	isDefault: z.boolean().default(false),
});

type FormValues = z.infer<typeof channelSchema>;

interface ChannelFormProps {
	channel?: Channel;
	onSuccess: () => void;
}

export const ChannelForm: React.FC<ChannelFormProps> = ({
	channel,
	onSuccess,
}) => {
	const { toast } = useToast();
	const { createChannel, loading: creating } = useCreateChannel();
	const { updateChannel, loading: updating } = useUpdateChannel();

	const form = useForm<FormValues>({
		resolver: zodResolver(channelSchema),
		defaultValues: {
			name: channel?.name ?? "",
			description: channel?.description ?? "",
			type: channel?.type ?? "TEXT",
			emoji: channel?.emoji ?? "",
			isDefault: channel?.isDefault ?? false,
		},
	});

	const onSubmit = async (values: FormValues) => {
		try {
			if (channel) {
				await updateChannel(channel.id, values);
				toast({
					title: "チャンネルを更新しました",
					variant: "default",
				});
			} else {
				await createChannel(values);
				toast({
					title: "チャンネルを作成しました",
					variant: "default",
				});
			}
			onSuccess();
		} catch (error) {
			toast({
				title: `チャンネルの${channel ? "更新" : "作成"}に失敗しました`,
				variant: "destructive",
			});
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>チャンネル名</FormLabel>
							<FormControl>
								<Input {...field} placeholder="例: general" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormLabel>説明</FormLabel>
							<FormControl>
								<Textarea
									{...field}
									placeholder="チャンネルの説明を入力してください"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="type"
					render={({ field }) => (
						<FormItem>
							<FormLabel>種類</FormLabel>
							<Select onValueChange={field.onChange} defaultValue={field.value}>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder="チャンネルの種類を選択" />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									<SelectItem value="TEXT">一般</SelectItem>
									<SelectItem value="ANNOUNCEMENT">お知らせ</SelectItem>
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="emoji"
					render={({ field }) => (
						<FormItem>
							<FormLabel>絵文字</FormLabel>
							<FormControl>
								<Input {...field} placeholder="例: 👋" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="flex justify-end space-x-2">
					<Button type="submit" disabled={creating || updating}>
						{creating || updating ? "送信中..." : channel ? "更新" : "作成"}
					</Button>
				</div>
			</form>
		</Form>
	);
};
